import { Injectable } from '@angular/core';
import { CookieOptions, CookieService } from 'ngx-cookie-service';
import { NGXLogger } from 'ngx-logger';

export interface SoldoTrackingCookie {
    CID: string;
    PID: string;
    LID: string;
    OID: string;
    SID: string;
    sld_aff: string;
    sld_aff_t: number;
    gclid: string;
    gclid_timestamp: number;
    email: string;
    phone: string;
    AFF_META: { [key: string]: unknown };
    META: { [key: string]: unknown };
    experimentId: string;
    experimentVariantId: string;
}

export interface SoldoMarketingParameters {
    CID?: string;
    PID?: string;
    LID?: string;
    OID?: string;
    SID?: string;
    utm_source: string;
    utm_campaign: string;
    utm_content: string;
    utm_term: string;
    utm_medium: string;
    driverid: string;
}

export interface SoldoAffiliateData {
    affiliateId: string;
    affiliateClickTime: number;
    affiliateMetadata: string;
}

export interface GCLIDData {
    gclid: string;
    gclidTimestamp: number;
}

export interface SoldoRegistrationConfigData {
    discountCode: string;
    geolocation: string;
    idvSessionId: string;
    idvProvider: string;
    idvPersonExternalId: string;
}

export interface SignupWithXeroConfigurationCookie {
    plan: string;
    offer: string;
}

const AFFILIATE_ID_PARAM = "sld_aff";

const SECONDS_MS = 1000;
const MINUTES_MS = 60 * SECONDS_MS;
const DAYS_MS = 24 * SECONDS_MS * MINUTES_MS;

const SOLDO_REGISTRATION_CONFIG_COOKIE_NAME = "soldo-registration-config";

const SOLDO_JWT_NAME = "access_token";
const SOLDO_JWT_DURATION = 7 * DAYS_MS;

const SOLDO_SIGNUP_WITH_XERO_CONF_COOKIE_NAME = "sld_swxc";
const SOLDO_SIGNUP_WITH_XERO_CONF_COOKIE_DURATION = 7 * DAYS_MS;
const SOLDO_SIGNUP_WITH_XERO_REFERENCE_COOKIE_NAME = "sld_swxr";

export const SOLDO_COOKIE_OPTIONS: CookieOptions = {
    path: "/",
    domain: ".soldo.com",
    secure: true,
    sameSite: "Strict"
};

const CONFIG_COOKIE_DURATION = 30 * MINUTES_MS;

const SOLDO_GTM_APPLICATION_EXTERNAL_ID_COOKIE_NAME = "sld_rid";
const SOLDO_GTM_APPLICATION_EXTERNAL_ID_COOKIE_DURATION = DAYS_MS;

const SOLDO_EMAIL_VERIFICATION_REQUEST_COOKIE_NAME = "sld_evr";
const SOLDO_EMAIL_VERIFICATION_REQUEST_COOKIE_DURATION = 31 * SECONDS_MS;

const AFFILIATE_META_PARAM_EXCLUSION = [
    AFFILIATE_ID_PARAM,
    "registrationId",
    "OfferName",
    "PlanName",
    "discountCode",
];

const DEDICATED_UTM_COOKIE_NAME = "sld_utm";
const DEDICATED_GCLID_COOKIE_NAME = "sld_gclid";
const DEDICATED_AFFILIATE_COOKIE_NAME = "sld_aff";

interface AffiliateTrackingCookie {
    id: string,
    timestamp: number,
    [key: string]: unknown
}
@Injectable()
export class SoldoCookieService {

    constructor(
        private readonly cookieService: CookieService,
        private readonly logger: NGXLogger
    ) { }

    getStoredConfig(): Partial<SoldoRegistrationConfigData> {
        const conf: Partial<SoldoRegistrationConfigData> = {
            discountCode: null,
            geolocation: null,
            idvSessionId: null,
            idvProvider: null,
            idvPersonExternalId: null
        };
        const encoded = this.cookieService.get(SOLDO_REGISTRATION_CONFIG_COOKIE_NAME);
        if (encoded) {
            try {

                const serialized = atob(encoded);
                const jsonConf = JSON.parse(serialized);

                conf.discountCode = jsonConf.discountCode;
                conf.geolocation = jsonConf.geolocation ?? null;
                conf.idvSessionId = jsonConf.idvSessionId ?? null;
                conf.idvProvider = jsonConf.idvProvider ?? null;
                conf.idvProvider = jsonConf.idvProvider ?? null;
                conf.idvPersonExternalId = jsonConf.idvPersonExternalId ?? null;
            } catch (e) {
                this.logger.debug("Can't eval stored config cookie", e);
            }
        }

        return conf;
    }

    storeConfig(conf: Partial<SoldoRegistrationConfigData>): void {
        const serialized = JSON.stringify(conf);
        const encoded = btoa(serialized);
        this.cookieService.set(
            SOLDO_REGISTRATION_CONFIG_COOKIE_NAME,
            encoded,
            { ...SOLDO_COOKIE_OPTIONS, expires: new Date(Date.now() + CONFIG_COOKIE_DURATION) });
    }

    updateAffiliateData(params: URLSearchParams): SoldoAffiliateData {
        if (params.get(AFFILIATE_ID_PARAM)) {
            const affiliateData: AffiliateTrackingCookie = {
                id: params.get(AFFILIATE_ID_PARAM),
                timestamp: Date.now()
            };
            for (const [key, value] of params) {
                if (!AFFILIATE_META_PARAM_EXCLUSION.includes(key)) {
                    affiliateData[key] = value;
                }
            }
            this.updateDedicatedAffiliateTrackingCookie(affiliateData);
        }
        return this.readDedicatedAffiliateTrackingCookie();
    }

    readDedicatedUTMCookie(): SoldoMarketingParameters {
        const serializedCookieValue = this.cookieService.get(DEDICATED_UTM_COOKIE_NAME);
        if (serializedCookieValue) {
            const utmValuesArray = serializedCookieValue.split(";");
            return {
                utm_source: utmValuesArray[0],
                utm_medium: utmValuesArray[1],
                utm_campaign: utmValuesArray[2],
                utm_content: utmValuesArray[3],
                utm_term: utmValuesArray[4],
                driverid: utmValuesArray[5]
            };
        }
        return null;
    }

    readDedicatedGoogleClickIDCookie(): GCLIDData {
        const serializedCookieValue = this.cookieService.get(DEDICATED_GCLID_COOKIE_NAME);
        if (serializedCookieValue) {
            const gclidValuesArray = serializedCookieValue.split(":");
            return {
                gclid: gclidValuesArray[0],
                gclidTimestamp: parseInt(gclidValuesArray[1])
            };
        }
        return null;
    }

    private updateDedicatedAffiliateTrackingCookie(data: AffiliateTrackingCookie): void {
        if (typeof data == 'object') {
            this.cookieService.set(DEDICATED_AFFILIATE_COOKIE_NAME,
                btoa(JSON.stringify(data)),
                { ...SOLDO_COOKIE_OPTIONS, expires: new Date(Date.now() + (DAYS_MS * 30)) });
        }
    }

    readDedicatedAffiliateTrackingCookie(): SoldoAffiliateData {
        const serializedCookieValue = this.cookieService.get(DEDICATED_AFFILIATE_COOKIE_NAME);
        if (serializedCookieValue) {
            try {
                const affiliateCookieObject: AffiliateTrackingCookie = JSON.parse(atob(serializedCookieValue));
                this.logger.info("sld_aff cookie", affiliateCookieObject);

                const affiliateDataObject: SoldoAffiliateData = {
                    affiliateId: affiliateCookieObject.id,
                    affiliateClickTime: affiliateCookieObject.timestamp,
                    affiliateMetadata: ""
                };

                const metaDataKeys = Object.keys(affiliateCookieObject);
                const metaDataObject = {};
                metaDataKeys.forEach((key) => {
                    if (!["id", "timestamp"].includes(key)) {
                        metaDataObject[key] = affiliateCookieObject[key];
                    }
                });

                affiliateDataObject.affiliateMetadata = JSON.stringify(metaDataObject);

                return affiliateDataObject;
            } catch (e) {
                this.logger.error("Could not parse affiliate tracking cookie", serializedCookieValue, e);
            }
        }
        return null;
    }

    storeJWT(token: string) {
        this.cookieService.set(SOLDO_JWT_NAME, token, { ...SOLDO_COOKIE_OPTIONS, expires: new Date(Date.now() + SOLDO_JWT_DURATION) });
    }

    readJWT(): string {
        return this.cookieService.get(SOLDO_JWT_NAME);
    }

    cleanJWT(): void {
        this.cookieService.delete(SOLDO_JWT_NAME, SOLDO_COOKIE_OPTIONS.path, SOLDO_COOKIE_OPTIONS.domain, SOLDO_COOKIE_OPTIONS.secure, SOLDO_COOKIE_OPTIONS.sameSite);
    }

    hasJWT(): boolean {
        return this.cookieService.check(SOLDO_JWT_NAME);
    }

    writeSignupWithXeroCookie(plan: string, offer: string) {
        if (plan && offer) {
            const serializedCookieValue = [plan, offer].join(":");
            this.cookieService.set(SOLDO_SIGNUP_WITH_XERO_CONF_COOKIE_NAME,
                serializedCookieValue,
                { ...SOLDO_COOKIE_OPTIONS, expires: new Date(Date.now() + SOLDO_SIGNUP_WITH_XERO_CONF_COOKIE_DURATION) });
        }
    }

    readSignupWithXeroCookie(): SignupWithXeroConfigurationCookie {
        const serializedCookieValue = this.cookieService.get(SOLDO_SIGNUP_WITH_XERO_CONF_COOKIE_NAME);
        let cookieValue: SignupWithXeroConfigurationCookie = null;
        if (serializedCookieValue?.includes(":")) {
            const splitValue = serializedCookieValue.split(":");
            cookieValue = {
                plan: splitValue[0],
                offer: splitValue[1]
            };
        }
        return cookieValue;
    }

    writeSignupWithXeroReferenceCookie(reference: string) {
        this.cookieService.set(SOLDO_SIGNUP_WITH_XERO_REFERENCE_COOKIE_NAME,
            reference,
            { ...SOLDO_COOKIE_OPTIONS, expires: new Date(Date.now() + SOLDO_SIGNUP_WITH_XERO_CONF_COOKIE_DURATION) })
    }

    readSignupWithXeroReferenceCookie(): string {
        return this.cookieService.get(SOLDO_SIGNUP_WITH_XERO_REFERENCE_COOKIE_NAME);
    }

    storeGTMExternalId(extId: string) {
        this.cookieService.set(SOLDO_GTM_APPLICATION_EXTERNAL_ID_COOKIE_NAME,
            extId,
            { ...SOLDO_COOKIE_OPTIONS, expires: new Date(Date.now() + SOLDO_GTM_APPLICATION_EXTERNAL_ID_COOKIE_DURATION) });
    }

    storeEmailVerificationRequestCookie(email: string): void {
        this.cookieService.set(SOLDO_EMAIL_VERIFICATION_REQUEST_COOKIE_NAME,
            email,
            { ...SOLDO_COOKIE_OPTIONS, expires: new Date(Date.now() + SOLDO_EMAIL_VERIFICATION_REQUEST_COOKIE_DURATION) });
    }

    readEmailVerificationRequestCookie(): string | null {
        return this.cookieService.get(SOLDO_EMAIL_VERIFICATION_REQUEST_COOKIE_NAME) || null;
    }


}
